import React, { ChangeEvent, useEffect, useState } from 'react';
import { Select } from '@components/Select';
import { SimpleSelect } from '@components/SimpleSelect';
import { CountryItem, IndustryItem, SublistItem } from '@type/common';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { NewsAndStoriesFilter as NewsAndStoriesFilterType } from '../../NewsAndStories.types';
import { getItemFromList, getItemFromSubList } from '@pages/NewsAndStories/NewsAndStories.helpers';
import './NewsAndStoriesFilter.scss';
import { useUpdateEffect } from '@hooks/useUpdateEffect';

interface NewsAndStoriesFilterProps {
  i18n: Record<string, string>;
  resetCount: number;
  categories: SublistItem[];
  countries: CountryItem[];
  years: number[];
  industries: IndustryItem[];
  selectorTitles: {
    type: string;
    industry: string;
    country: string;
    year: string;
  };
  filter: NewsAndStoriesFilterType;
  isDefaultOpen?: boolean;
  onChange: (
    name: keyof NewsAndStoriesFilterType,
    value: NewsAndStoriesFilterType[keyof NewsAndStoriesFilterType]
  ) => void;
}

export const NewsAndStoriesFilter = ({
  i18n,
  resetCount,
  categories = [],
  countries = [],
  years = [],
  industries = [],
  filter,
  onChange,
  isDefaultOpen = false,
  selectorTitles,
}: NewsAndStoriesFilterProps) => {
  const [searchString, setSearchString] = useState(filter.searchString);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(isDefaultOpen);
  const [isKeywordsDirty, setIsKeywordsDirty] = useState(false);

  useUpdateEffect(() => {
    setSearchString('');
  }, [resetCount]);

  const yearsListFormatted = [
    { Name: i18n.AllYears, Id: 'All' },
    ...years.map((year) => ({ Name: year.toString(), Id: year.toString() })),
  ];

  const categoriesTitle =
    selectorTitles.type || getItemFromList(filter.category?.Id, categories)?.Name;
  const industriesTitle =
    selectorTitles.industry ||
    getItemFromSubList(filter.industry?.Id, industries, 'SubIndustries')?.Name;
  const countriesTitle =
    selectorTitles.country || getItemFromSubList(filter.country?.Id, countries, 'Countries')?.Name;
  const yearsTitle =
    selectorTitles.year || getItemFromList(filter.year?.toString(), yearsListFormatted)?.Name;

  const onAdvancedFilterBtnClick = () => {
    setIsAdvancedFilterOpen(!isAdvancedFilterOpen);
  };

  return (
    <div className='news-and-stories-filter'>
      <div className='news-and-stories-filter-standard'>
        <Input
          type='text'
          value={searchString}
          name='searchForKeywords'
          placeholder={i18n.Keywords}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchString(e.target.value);
            setIsKeywordsDirty(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onChange('searchString', searchString);
            }
          }}
        />
        <div className='news-and-stories-filter-controls'>
          <Button
            size='small'
            disabled={!isKeywordsDirty}
            text='Filter'
            color='primary'
            className='news-and-stories-filter__filter-button'
            onClick={() => {
              onChange('searchString', searchString);
            }}
          />
          <a className='news-and-stories-filter__advanced-link' onClick={onAdvancedFilterBtnClick}>
            {isAdvancedFilterOpen ? i18n.HideAdvancedFilters : i18n.AdvancedFilters}
          </a>
        </div>
      </div>

      {isAdvancedFilterOpen && (
        <div className='news-and-stories-filter-advanced'>
          <SimpleSelect
            title={categoriesTitle || i18n.Type}
            list={categories}
            name='Name'
            selectFunction={(item) => {
              if (item.Id === 'All') {
                onChange('category', undefined);
                return;
              }
              onChange('category', item);
            }}
          />
          <Select
            selectableTitle
            defaultTitle={i18n.AllIndustries}
            resetTime={resetCount}
            title={industriesTitle || i18n.Industry}
            backLabel={i18n.Back}
            list={industries}
            name='Name'
            sub='SubIndustries'
            selectFunction={(item) => {
              onChange('industry', item);
            }}
          />
          <Select
            selectableTitle
            defaultTitle={i18n.AllCountries}
            resetTime={resetCount}
            title={countriesTitle || i18n.Location}
            backLabel={i18n.Back}
            list={countries}
            name='Name'
            sub='Countries'
            selectFunction={(item) => {
              onChange('country', item);
            }}
          />
          <SimpleSelect
            title={yearsTitle || i18n.Year}
            list={yearsListFormatted}
            name='Name'
            selectFunction={(item) => {
              if (item.Id === 'All') {
                onChange('year', undefined);
              } else {
                onChange('year', item.Id);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};
